import { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import SiteHeader from "@/components/SiteHeader";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { KeyRound, Loader2, ArrowLeft } from "lucide-react";

export default function Login() {
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from || "/curator";

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await login(password);
      navigate(from, { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Login failed. Try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen">
      <SiteHeader />
      <div className="max-w-7xl mx-auto px-6 md:px-12 pt-12">
        <Link
          to="/"
          data-testid="login-back-link"
          className="inline-flex items-center gap-2 font-ui text-xs tracking-eerie uppercase text-[#a89f91] hover:text-[#e8aa42] transition-colors duration-500"
        >
          <ArrowLeft className="w-4 h-4" strokeWidth={1.25} />
          Back to the Exhibit
        </Link>
      </div>

      <div className="max-w-md mx-auto px-6 py-20 md:py-28">
        <div className="text-center mb-12">
          <KeyRound className="w-8 h-8 text-[#e8aa42] mx-auto mb-6 candle-flicker" strokeWidth={1.25} />
          <p className="font-ui text-xs tracking-eerie uppercase text-[#e8aa42]/80 mb-3">
            Curator's Gate
          </p>
          <h1 className="font-display font-light text-5xl text-[#e2d6c8] mb-4">
            Speak the Word
          </h1>
          <p className="font-body text-base text-[#a89f91]">
            Only Vicky may tend to the girls.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6" data-testid="login-form">
          <div>
            <Label
              htmlFor="password"
              className="font-ui text-xs tracking-eerie uppercase text-[#a89f91]"
            >
              Password
            </Label>
            <Input
              id="password"
              data-testid="login-password-input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoFocus
              required
              className="mt-3 bg-[#1a1614] border-[#e8aa42]/25 text-[#e2d6c8] font-body text-lg h-14 focus-visible:ring-[#e8aa42]/40 focus-visible:border-[#e8aa42]/60"
              placeholder="…"
            />
          </div>

          {error && (
            <p
              data-testid="login-error"
              className="font-ui text-xs tracking-eerie uppercase text-[#8b0000] border border-[#8b0000]/40 bg-[#8b0000]/10 py-3 px-4"
            >
              {error}
            </p>
          )}

          <Button
            type="submit"
            data-testid="login-submit-btn"
            disabled={busy || !password}
            className="w-full bg-[#8b0000] hover:bg-[#5c0000] text-[#e2d6c8] font-ui text-xs tracking-eerie uppercase rounded-none h-12 border border-[#8b0000] hover:border-[#e8aa42]/60 transition-colors duration-500"
          >
            {busy ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Opening…
              </>
            ) : (
              "Enter the Cabinet"
            )}
          </Button>
        </form>
      </div>
    </div>
  );
}
